import { useIsFetching, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { UserInfoContext } from "../context/UserInfoContext";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import DateUtils from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/DateUtils";
import { Routes } from "../types/Routes";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import { WorklogReportSend } from "../models/WorklogReportSend";
import Assignation from "../models/Assignation";
import Holiday from "../models/Holiday";
import Position from "../models/Position";
import Timeoff from "../models/Timeoff";
import WorkLog from "../models/WorkLog";
import AssignationService from "../services/AssignationService";
import HolidayService from "../services/HolidayService";
import PositionService from "../services/PositionService";
import ProjectService from "../services/ProjectService";
import WorkLogService from "../services/WorkLogService";
import AssignationPauseService from "../services/AssignationPauseService";
import { WorlogWeeksDaysFilters } from "../utils/enums/filters/WorklogFilters";
import { TimeOffStatus } from "../utils/enums/TimeOffStatus";
import OpsDateUtils from "../utils/OpsDateUtils";
import OpsStringUtils from "../utils/OpsStringUtils";
import RoleUtils from "../utils/RoleUtils";
import TimeOffUtils from "../utils/TimeOffUtils";
import { Country } from "../utils/Countries";
import useUpdateUrl from "./useUpdateUrl";
import { useStoredDate } from "../hooks/useStoredDate";
import { QueryKeyFactory, WORKLOG_DAILY_LIST_CONTEXT } from "../services/queryKeys";
import { rethrowWithFallback, rethrowWithMessage, unwrap } from "../utils/queryFn";
import { createTableQuery, refreshTableQuery } from "../utils/tableQuery";
import { fetchOpsUserByCoreId } from "./useUserInfo";

/** One row per day. 31 is the longest month, so one page can hold the whole month. */
export const WORKLOG_PAGE_SIZE = 31;
const workLogKeys = new QueryKeyFactory(WorkLogService.path);

const useWorkLogList = () => {
  const loggedUserInfo = useContext(UserInfoContext); // this represent the logged user
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [scrollToDay, setScrollToDay] = useState<number | undefined>(undefined);
  const [onlyWeekDays, setOnlyWeekDays] = useState<string>(WorlogWeeksDaysFilters.CALENDAR_WEEK);
  // User, active assignations and position information
  const [user, setUser] = useState<EnsolversOpsUser | undefined>(undefined); // this is the owner user of the worklogs
  const [userTimeoffs, setUserTimeoffs] = useState<Timeoff[]>();
  const [assignations, setAssignations] = useState<AutoCompleteUIDTO[]>([]);
  const [selectedAssignation, setSelectedAssignation] = useState<AutoCompleteUIDTO>(EMPTY_AUTOCOMPLETE);
  const [currPosition, setCurrPosition] = useState<Position | undefined>(undefined);
  const [assignation, setAssignation] = useState<Assignation | undefined>(undefined);
  // worklog entries related states
  const [results, setResults] = useState([] as WorkLog[]); // TODO type this results
  const [workLog, setWorkLog] = useState<WorkLog | undefined>(undefined);
  const [selectedWorkLog, setSelectedWorkLog] = useState<WorkLog | undefined>(undefined);
  const [isManagerOfProject, setIsManagerOfProject] = useState<boolean>(false);
  const [formDialog, setFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [forceUpdateOn, setForceUpdateOn] = useState<number>();
  const [showOnlyMissing, setShowOnlyMissing] = useState(false);
  // Reports data
  const [slackReportDialog, setSlackReportDialog] = useState<WorklogReportSend | undefined>(undefined);
  const [unified, setUnified] = useState<boolean>(false);
  // SelectedDate and holidays included in that month and year
  const [selectedDate, setSelectedDate] = useStoredDate();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  // URL Params and update function
  const searchParams = new URLSearchParams(useLocation().search);
  const queryYear = searchParams.get("year");
  const queryMonth = searchParams.get("month");
  const queryDay = searchParams.get("day");
  const userExternalId = searchParams.get("user");
  const paramAssignationExternalId = searchParams.get("assignation");
  const { updateUrl } = useUpdateUrl(Routes.WORKLOG, searchParams);
  const history = useHistory();

  const queryClient = useQueryClient();
  const isFetchingWorklogs = useIsFetching({ queryKey: workLogKeys.lists(WORKLOG_DAILY_LIST_CONTEXT) }) > 0;
  const scrollToDayRef = useRef(scrollToDay);
  const isFirstComponentLoadRef = useRef(true);
  const loadedAssignationIdRef = useRef<string | undefined>(undefined);
  const holidayCacheRef = useRef(new Map<string, Holiday[]>());
  const holidayFlightRef = useRef(new Map<string, Promise<Holiday[]>>());

  const isMissingDay = (instance: WorkLog) =>
    !instance.externalId &&
    !instance.isWeekend &&
    !instance.isHoliday &&
    !instance.isPaused &&
    !isTimeOffDay(instance) &&
    isAssignationActive(instance);

  const isTimeOffDay = (instance: WorkLog) =>
    !!userTimeoffs?.some((timeoff) =>
      OpsDateUtils.isBetweenOrEqual(timeoff.shortStartDate, timeoff.shortEndDate, OpsDateUtils.toUTCWithSetHours(instance.shortDay)),
    );

  // On the first load of the component:
  // if minimum params are not present: replace URL with default params based on logged user
  // otherwise load the information based on the params present
  useEffect(() => {
    // This condition will ensure that the component will only load once and after the user information is loaded
    if (!isFirstComponentLoadRef.current || !loggedUserInfo.isUserLoaded()) {
      return;
    }
    isFirstComponentLoadRef.current = false;

    queryDay && setScrollToDay(parseInt(queryDay));
    if (!queryMonth || !queryYear || !userExternalId) {
      writeWorklogUrl();
    } else {
      (async () => {
        await loadUserData(userExternalId);
      })();
    }
  }, [loggedUserInfo]);

  useEffect(() => {
    scrollToDayRef.current = scrollToDay;

    return () => {
      scrollToDayRef.current = undefined;
    };
  }, [scrollToDay]);

  // After finishing loading the data, if there's a day param, wait 1sec and then scrolls to that day
  useEffect(() => {
    if (!isLoading && scrollToDay) {
      setTimeout(scrollToSelectedDay, 1000);
    }
  }, [isLoading]);

  // Once the Owner user of the worklogs is set, fetch its assignations and set the selected one. If no assignation param is present, select the first one available
  useEffect(() => {
    if (!!user) {
      (async () => {
        try {
          const assigs = await getAssignationsByUser(user.coreUserExternalId!, selectedDate);
          const list = Array.isArray(assigs) ? assigs : [];
          setAssignations(list);
          const matchingAssig = paramAssignationExternalId
            ? list.find((item) => item.externalId === paramAssignationExternalId)
            : undefined;
          const next = matchingAssig ?? list[0] ?? EMPTY_AUTOCOMPLETE;
          setSelectedAssignation(next);
          if (!paramAssignationExternalId && next.externalId) {
            writeWorklogUrl(next.externalId);
          }
        } catch (error) {
          NotificationService.error("Error finding Assignation");
          setIsLoading(false);
        }
      })();
    }
    // selectedDate is read when the owner arrives; a later month change goes through writeWorklogUrl.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Same assignation id with a new object (the year change refreshes the name list) must not reload the detail.
  // An empty selection still has to clear the initial spinner.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadSelectedAssignation();
  }, [selectedAssignation]);

  const loadSelectedAssignation = async () => {
    const assignationId = selectedAssignation?.externalId;
    if (!assignationId || assignationId === EMPTY_AUTOCOMPLETE.externalId) {
      setIsLoading(false);
      return;
    }
    if (loadedAssignationIdRef.current === assignationId) {
      return;
    }
    try {
      if (loggedUserInfo.isUserLoaded()) {
        checkIfUserIsManagerFromProject();
      }
      if (!!selectedAssignation.externalId) {
        !isLoading && setIsLoading(true);

        let assignationResponse = await AssignationService.getByExternalId(selectedAssignation.externalId);
        const assignation = assignationResponse.getContentOrThrowError();
        let positionResponse = await PositionService.getByExternalId(assignation.position.externalId!);

        setCurrPosition(positionResponse.getContentOrThrowError());
        setAssignation(assignation);
        loadedAssignationIdRef.current = assignationId;

        user && (await loadHolidays(selectedDate, user.country ? user.country : Country.ARGENTINA));

        TimeOffUtils.getUserTimeoffs(selectedAssignation.externalId, selectedDate).then((timeoffs) => {
          setUserTimeoffs(timeoffs);
        });
      }
    } catch (error) {
      NotificationService.error("Error getting position");
    } finally {
      setIsLoading(false);
    }
  };

  // Update the URL params
  /**
   * UPdate the URL and its params based on the selected information and update the data related
   * @param selectedAssignationExternalId
   * @param selectedDate
   */
  const selectAssignation = (next: AutoCompleteUIDTO) => {
    setSelectedAssignation(next);
    if (next.externalId && next.externalId !== paramAssignationExternalId) {
      writeWorklogUrl(next.externalId);
    }
  };

  const writeWorklogUrl = (selectedAssignationExternalId?: string, nextDate?: Date) => {
    if (!loggedUserInfo.isUserLoaded()) {
      return;
    }

    let queryDate = new Date(selectedDate);
    if (nextDate) {
      queryDate = new Date(nextDate);
    } else {
      queryYear && queryDate.setFullYear(parseInt(queryYear ?? selectedDate.getFullYear().toString()));
      queryMonth ? queryDate.setMonth(parseInt(queryMonth) - 1) : queryDate.setMonth(selectedDate.getMonth());
    }

    setSelectedDate(queryDate);

    // establish searchParams
    searchParams.set("year", queryDate.getFullYear().toString());
    searchParams.set("month", (queryDate.getMonth() + 1).toString());

    searchParams.set("user", userExternalId ?? loggedUserInfo.user.externalId);
    // establish assignation param
    if (selectedAssignationExternalId && selectedAssignationExternalId !== paramAssignationExternalId) {
      searchParams.set("assignation", selectedAssignationExternalId);
    } else {
      paramAssignationExternalId && searchParams.set("assignation", paramAssignationExternalId);
    }

    loadUserData(searchParams.get("user")!);

    updateUrl(Routes.WORKLOG, searchParams);

    if (user && nextDate) {
      getAssignationsByUser(user.coreUserExternalId!, nextDate).then((res) => {
        if (paramAssignationExternalId) {
          const matchingAssig = res.find((a) => a.externalId === paramAssignationExternalId);
          setSelectedAssignation(matchingAssig!);
        } else {
          setSelectedAssignation(res ? res[0] : EMPTY_AUTOCOMPLETE);
        }
        setAssignations(res);
      });
    }
  };

  /**
   * If the month or year changes, update the selected assignation information
   * @param newDate Date object to compare with current {@link selectedDate}
   */
  const changeDate = async (newDate: Date | null) => {
    if (newDate && (newDate.getMonth() !== selectedDate.getMonth() || newDate?.getFullYear() !== selectedDate.getFullYear())) {
      writeWorklogUrl(selectedAssignation?.externalId, newDate);
    }
  };

  /**
   * Holidays for a month and country. One request per key; the worklog filler reuses it.
   */
  const loadHolidays = (date: Date, country: string): Promise<Holiday[]> => {
    const key = `${date.getFullYear()}:${date.getMonth() + 1}:${country}`;
    const cached = holidayCacheRef.current.get(key);
    if (cached) return Promise.resolve(cached);
    const inflight = holidayFlightRef.current.get(key);
    if (inflight) return inflight;

    const pending = HolidayService.findHolidaysOfMonthByCountry(date.getMonth() + 1, country).then((response) => {
      holidayFlightRef.current.delete(key);
      if (!response.isSuccess()) {
        const message = response.getMessage();
        NotificationService.error(typeof message === "string" && message ? message : "Error fetching holidays");
        return [];
      }
      const content = response.getContentOrThrowError() ?? [];
      content.forEach((holiday: Holiday) => {
        holiday.shortDate = OpsDateUtils.utcOffset(holiday.shortDate);
      });
      holidayCacheRef.current.set(key, content);
      setHolidays(content);
      return content;
    });
    holidayFlightRef.current.set(key, pending);
    return pending;
  };

  /**
   * Get all the assignations that are active for the User and the date given
   * @param userExternalId COre User externalId
   * @param date Date object on which to find active Assignations ( Important information are Month nad Year )
   * @returns Active Assignations in format of {@link AutoCompleteUIDTO[]}
   */
  const getAssignationsByUser = async (userExternalId: string, date: Date): Promise<AutoCompleteUIDTO[]> => {
    const response = await AssignationService.findAssignationsNamesByUser(userExternalId, date);
    return response.getContentOrThrowError();
  };

  /**
   * Fetch the User information and update state
   * @param externalId Core User ExternalId
   */
  const loadUserData = async (externalId: string) => {
    try {
      if (externalId && (!user || externalId !== user.coreUserExternalId)) {
        !isLoading && setIsLoading(true);
        const content = await fetchOpsUserByCoreId(queryClient, externalId);
        setUser(content);
      }
    } catch (error) {
      // QueryClient already toasts the ApiError.
      setIsLoading(false);
    }
  };

  /**
   * Verify if the logged user is Manager of the Assignation's Project. It also applies to their own worklog, where they can approve weeks too.
   */
  const checkIfUserIsManagerFromProject = async () => {
    if (
      RoleUtils.isManager() && // the endpoint is manager/admin only, so a contractor on their own worklog must not call it
      !RoleUtils.isAdmin() && // is not admin
      selectedAssignation && // has a selected assignation
      selectedAssignation.externalId !== EMPTY_AUTOCOMPLETE.externalId // with a non empty value
    ) {
      const response = await ProjectService.isUserCoreManagerFromProjectByAssignation(
        selectedAssignation.externalId,
        loggedUserInfo.user.externalId,
      );
      setIsManagerOfProject(response.isSuccess());
    }
  };

  const refreshWorklogs = () =>
    refreshTableQuery(workLogKeys.lists(WORKLOG_DAILY_LIST_CONTEXT), () => setForceUpdateOn(Date.now()), queryClient);

  // Holidays depend on the owner's country. Until that user exists, a fetch would cache Argentina and stick for 60s.
  const holidayCountry = user ? user.country || Country.ARGENTINA : undefined;

  const loadWorklogPage = async (pageNumber: number, count: number): Promise<PagedResult<WorkLog>> => {
    if (!paramAssignationExternalId || !queryYear || !queryMonth || !holidayCountry) {
      return PagedResult.empty();
    }

    const year = parseInt(queryYear);
    const month = parseInt(queryMonth) - 1;
    let pausedDays: Set<string> = new Set();
    try {
      const pausedResponse = await AssignationPauseService.getPausedDays(paramAssignationExternalId, year, month);
      const pausedList = pausedResponse.getContentOrElse(() => []);
      if (!pausedResponse.isSuccess()) {
        NotificationService.error(pausedResponse.getMessage());
      }
      pausedDays = new Set(pausedList.map((date: Date) => OpsDateUtils.formatInUTCAsDDMMMYYY(date)));
    } catch (error) {
      NotificationService.error("Error fetching paused days");
    }

    const monthHolidays = await loadHolidays(new Date(year, month, 1), holidayCountry);
    const result = await WorkLogService.getAllWithPrefilledEmptyPlaceholders(
      pageNumber,
      count,
      paramAssignationExternalId,
      year,
      month,
      userExternalId,
      monthHolidays,
    );

    const content = result.content.map((worklog: WorkLog) => {
      const shortDay = OpsDateUtils.utcOffset(worklog.shortDay);
      const isPaused = !worklog.externalId && pausedDays.has(OpsDateUtils.formatInUTCAsDDMMMYYY(shortDay));
      return { ...worklog, shortDay, isPaused };
    });
    return new PagedResult(content, pageNumber, content.length);
  };

  const fetchPage = useMemo(
    () =>
      createTableQuery<WorkLog>({
        queryKey: (page, pageSize, sortBy, sortDir) =>
          workLogKeys.list(
            {
              page,
              pageSize,
              sortBy: sortBy ?? "",
              sortDir: sortDir ?? "",
              assignationExternalId: paramAssignationExternalId ?? "",
              year: queryYear ?? "",
              month: queryMonth ?? "",
              userExternalId: userExternalId ?? "",
              country: holidayCountry ?? "",
            },
            WORKLOG_DAILY_LIST_CONTEXT,
          ),
        queryFn: (page, pageSize) => loadWorklogPage(page, pageSize),
        client: queryClient,
      }),
    // loadWorklogPage closes over the URL period, the assignation, and the holiday loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, paramAssignationExternalId, queryYear, queryMonth, userExternalId, holidayCountry],
  );

  const onDataFetch = useCallback(
    async (pageNumber: number, _count: number, sortBy?: string, sortDir?: string): Promise<PagedResult<WorkLog>> => {
      if (!holidayCountry) {
        return PagedResult.empty();
      }
      const pageResult = await fetchPage(pageNumber, WORKLOG_PAGE_SIZE, sortBy, sortDir);
      setResults(pageResult.content);
      let filteredContent =
        onlyWeekDays === WorlogWeeksDaysFilters.WORKDAYS
          ? pageResult.content.filter((worklog: WorkLog) => !OpsDateUtils.isWeekend(OpsDateUtils.utcOffset(worklog.shortDay)))
          : pageResult.content;
      if (showOnlyMissing) {
        filteredContent = filteredContent.filter((row) => isMissingDay(row));
      }
      return new PagedResult(filteredContent, pageNumber, filteredContent.length);
    },
    // isMissingDay reads time off and the assignation range, which are not part of the query key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchPage, onlyWeekDays, showOnlyMissing, userTimeoffs, assignation, holidayCountry],
  );

  const deleteMutation = useMutation({
    mutationFn: async (workLogToDelete: WorkLog) => {
      try {
        return await unwrap(WorkLogService.delete(workLogToDelete));
      } catch (error) {
        return rethrowWithMessage(error, "Error trying to delete a worklog.");
      }
    },
    onSuccess: async () => {
      setSelectedWorkLog(undefined);
      await refreshWorklogs();
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (workLogToSave: WorkLog) => {
      try {
        return await unwrap(workLogToSave.externalId ? WorkLogService.update(workLogToSave) : WorkLogService.create(workLogToSave));
      } catch (error) {
        return rethrowWithFallback(error, workLogToSave.externalId ? "Error trying to update." : "WorkLog for that day already exists!");
      }
    },
    onSuccess: async (_saved, workLogToSave) => {
      NotificationService.info(workLogToSave.externalId ? "Worklog updated!" : "Worklog created!");
      await refreshWorklogs();
    },
  });

  const handleOnDeleteWorkLog = async () => {
    if (!selectedWorkLog) return;
    try {
      await deleteMutation.mutateAsync(selectedWorkLog);
    } catch {
      // QueryClient toasts the ApiError.
    }
  };

  const saveWorkLog = (workLogToSave: WorkLog) => saveMutation.mutateAsync(workLogToSave);

  /**
   * If day param is prensent await until all the informatio nis loaded, then scroll to that day
   */
  const scrollToSelectedDay = () => {
    if (!scrollToDayRef.current || !scrollToDay) return;

    const dayElement = document.getElementById(`day-${scrollToDay}`);
    if (!dayElement) return;

    dayElement.scrollIntoView({ behavior: "smooth" });

    // Clean state
    scrollToDayRef.current = undefined;
    setScrollToDay(undefined);
  };

  const isAssignationActive = (instance: WorkLog) => {
    const worklogDay = OpsDateUtils.toStartOfDay(instance.shortDay);

    // End date from the assignation and the position (if any)
    const assignationEnd = OpsDateUtils.toDate(assignation?.shortEndDate);
    const positionEnd = OpsDateUtils.toDate(assignation?.position?.shortEndDate);
    const effectiveEnd = OpsDateUtils.minDate(assignationEnd, positionEnd);

    if (effectiveEnd) {
      const endAsUtc = OpsDateUtils.makeLocalAppearUTC(effectiveEnd)!;
      const endDay = OpsDateUtils.toStartOfDay(endAsUtc);

      // If the worklog day is after the effective end date,
      // the assignation is no longer active
      const isBeforeOrOnEnd = OpsDateUtils.isSameOrBefore(worklogDay, endDay);
      if (!isBeforeOrOnEnd) return false;
    }

    // Start date from the assignation and the position (if any)
    const assignationStart = OpsDateUtils.toDate(assignation?.shortStartDate);
    const positionStart = OpsDateUtils.toDate(assignation?.position?.shortStartDate);
    const effectiveStart = OpsDateUtils.maxDate(assignationStart, positionStart);

    if (effectiveStart) {
      const startAsUtc = OpsDateUtils.makeLocalAppearUTC(effectiveStart)!;
      const startDay = OpsDateUtils.toStartOfDay(startAsUtc);

      // If the worklog day is before the effective start date,
      // the assignation is not active yet
      const isAfterOrOnStart = OpsDateUtils.isSameOrAfter(worklogDay, startDay);
      if (!isAfterOrOnStart) return false;
    }

    return true;
  };

  const handleOnClickRow = (instance: WorkLog) => {
    if (selectedAssignation === undefined) {
      NotificationService.error("First select an assignation");
      return;
    }

    if (!isAssignationActive(instance)) {
      NotificationService.error("Worklog out of assignation dates range");
      return;
    }

    if (!instance.externalId) {
      // New Instance Set Default Values
      instance.externalId = undefined;
      instance.hours = currPosition ? currPosition.externalHours : 8;
      instance.internalHours = currPosition ? currPosition.internalHours : 8;
      instance.assignationName = selectedAssignation.label;
      instance.assignationExternalId = selectedAssignation.externalId;

      handleTimeoffs(instance);
      setIsEditMode(false);
      setWorkLog(instance);
      setFormDialog(true);
    } else {
      // Edit Instance
      instance.assignationName = selectedAssignation.label;
      instance.assignationExternalId = selectedAssignation.externalId;

      setIsEditMode(true);
      setWorkLog(instance);
      setFormDialog(true);
    }
  };

  const handleTimeoffs = (instance: WorkLog) => {
    if (userTimeoffs) {
      userTimeoffs?.forEach((timeoff) => {
        if (OpsDateUtils.isBetweenOrEqual(timeoff.shortStartDate, timeoff.shortEndDate, instance.shortDay)) {
          let description: string;
          if (timeoff.status === TimeOffStatus.APPROVED) {
            description = "Timeoff: ".concat(
              OpsStringUtils.capitalize(timeoff.type),
              ", approved on ",
              DateUtils.formatAsDDMMMYYY(timeoff.approvedDate),
              ", by ",
              timeoff.approvedBy.firstName,
              " ",
              timeoff.approvedBy.lastName,
            );
          } else {
            description = "Timeoff: ".concat(OpsStringUtils.capitalize(timeoff.type));
          }
          instance.description = userTimeoffs ? "Timeoff" : "";
          instance.description = userTimeoffs && timeoff.halfDay ? "" : userTimeoffs ? "Timeoff" : "";
          instance.internalDescription = userTimeoffs ? description : "";
          instance.hours = userTimeoffs ? 0 : currPosition ? currPosition.externalHours : 8;
          instance.hours = currPosition && timeoff.halfDay ? currPosition.externalHours / 2 : currPosition ? currPosition.externalHours : 8;
          instance.internalHours =
            currPosition && timeoff.halfDay ? currPosition.externalHours / 2 : currPosition ? currPosition.externalHours : 8;
        }
      });
    }
  };

  return {
    user,
    assignation,
    selectedAssignation,
    setSelectedAssignation,
    currPosition,
    workLog,
    setWorkLog,
    formDialog,
    setFormDialog,
    isEditMode,
    setIsEditMode,
    assignations,
    isLoading,
    forceUpdateOn,
    selectedWorkLog,
    setSelectedWorkLog,
    unified,
    setUnified,
    slackReportDialog,
    setSlackReportDialog,
    holidays,
    userTimeoffs,
    selectedDate,
    setSelectedDate,
    isManagerOfProject,
    handleOnClickRow,
    isAssignationActive,
    handleOnDeleteWorkLog,
    saveWorkLog,
    refreshWorklogs,
    onDataFetch,
    selectAssignation,
    results,
    isFetchingWorklogs,
    userExternalId,
    handleTimeoffs,
    onlyWeekDays,
    setOnlyWeekDays,
    queryYear,
    queryMonth,
    paramAssignationExternalId,
    getAssignationsByUser,
    changeDate,
    showOnlyMissing,
    setShowOnlyMissing,
  };
};

export default useWorkLogList;
